import { PERF_TEST_TOPIC } from './constants';
import { PerfKit } from './perfkit';

import type { MTPerfkitConfig, IPerfkit } from './types';
import type { LoggerFactory } from '@amp/web-apps-logger';
import type { MTPerfkit } from '@amp-metrics/mt-perfkit';

type PerfKitOptions = {
    webVitals: boolean;
    loadUrl: boolean;
};

const DEFAULT_OPTIONS: PerfKitOptions = {
    webVitals: true,
    loadUrl: true,
};

export const setupBrowserPerfkit = (
    config: MTPerfkitConfig,
    loggerFactory: LoggerFactory,
    options: PerfKitOptions = DEFAULT_OPTIONS,
): IPerfkit => {
    const log = loggerFactory.loggerFor('Perfkit');
    const isMultiDev = window.location.href.includes('multidev');
    const BUILD_ENV = process.env.NODE_ENV;
    const isDevMode = isMultiDev || BUILD_ENV !== 'production';

    const initializePerfkit = async () => {
        if (typeof window === 'undefined') {
            throw new Error('perfkit can only be used in a browser context');
        }

        const topic =
            config.suppressPerfKit || isDevMode
                ? PERF_TEST_TOPIC
                : config.topic;
        let perfkitInstance: MTPerfkit | null = null;
        try {
            const [MTPerfkit, perfDelegates, clientConfig] = await Promise.all([
                import('@amp-metrics/mt-perfkit'),
                import('@amp-metrics/mt-metricskit-delegates-html'),
                import(
                    '@amp-metrics/mt-client-config/dist/mt-client-config.js'
                ),
            ]);
            perfkitInstance = MTPerfkit.default;

            // turning off all the logs
            perfkitInstance.system.logger.setLevel('none');
            perfDelegates.eventQueue.logger.setLevel('none');
            clientConfig.default.logger.setLevel('none');

            perfkitInstance.system.environment.setDelegate({
                app: () => config.appName,
                appVersion: () => config.appVersion,
                delegateApp: () => config.delegateApp,
                resourceRevNum: () => config.resourceRevNum,
            });

            //TODO: Move this to our "xp_amp_music_perf" config after launch
            // rdar://100912001 (Update perfkit config after launch)
            perfkitInstance.config.setDebugSource({
                maxPersistentQueueSize: 350,
                disabled: config.suppressPerfKit ?? isDevMode,
            });

            if (!config.suppressPerfKit) {
                // Init if instructed by user
                perfkitInstance.init(topic, null, perfDelegates);
            }

            if (config.suppressPerfKit || isDevMode) {
                log.debug('perfkit disabled. Will not send requests');
            }
        } catch (e) {
            log.error('perfkit failed to initialize', e);
        }
        return perfkitInstance;
    };

    const perfkit = new PerfKit(initializePerfkit(), log);

    if (options.webVitals) {
        perfkit.setupWebVitals();
    }

    if (options.loadUrl) {
        perfkit.setupLoadUrlEventObserver();
    }

    return perfkit;
};
